// Extracted verbatim from the v3 monolith (see BIRDSEYE.md module split plan).

import { ANIMATION_PRESETS } from './constants.js';

export function toArray(list) {
  return Array.prototype.slice.call(list || []);
}

export function parseJsonSafe(value, fallback) {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (_error) {
    return fallback;
  }
}

export function toInt(value, fallback) {
  var n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function toNonNegativeInt(value, fallback) {
  var n = parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export function toFloat(value, fallback) {
  var n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

export function toBool(value, fallback) {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  var normalized = String(value).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true;
  }

  if (normalized === 'false' || normalized === '0' || normalized === 'no') {
    return false;
  }

  return fallback;
}

export function isReducedMotion() {
  if (!window.matchMedia) {
    return false;
  }

  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_error) {
    return false;
  }
}

export function resolveAnimationPreset(name) {
  var key = normalizeText(name || 'smooth');
  return ANIMATION_PRESETS[key] || ANIMATION_PRESETS.smooth;
}

export function parseCsv(value) {
  if (!value) {
    return [];
  }

  return String(value)
    .split(',')
    .map(function (part) {
      return part.trim();
    })
    .filter(Boolean);
}

export function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function textFromNode(node) {
  if (!node) {
    return '';
  }

  var explicit = node.getAttribute('data-pf-value');
  if (explicit !== null) {
    return explicit;
  }

  return (node.textContent || '').trim();
}

export function pushUniqueNormalizedFieldValue(fields, key, rawValue) {
  var value = normalizeText(rawValue);
  if (!key || !value) {
    return;
  }

  if (!fields[key]) {
    fields[key] = [];
  }

  if (fields[key].indexOf(value) === -1) {
    fields[key].push(value);
  }
}

export function buildFieldLookup(fields) {
  var lookup = Object.create(null);

  Object.keys(fields || {}).forEach(function (field) {
    var values = fields[field] || [];
    if (!values.length) {
      return;
    }

    var valueMap = Object.create(null);
    values.forEach(function (value) {
      if (value) {
        valueMap[value] = true;
      }
    });

    lookup[field] = valueMap;
  });

  return lookup;
}

export function debounce(fn, delay) {
  var timer = null;

  return function () {
    var args = arguments;
    var context = this;

    if (timer) {
      window.clearTimeout(timer);
    }

    timer = window.setTimeout(function () {
      timer = null;
      fn.apply(context, args);
    }, delay);
  };
}

export function callHook(fn, payload, instance) {
  if (typeof fn !== 'function') {
    return;
  }

  try {
    fn(payload, instance);
  } catch (error) {
    console.error('[PandaFilter] Hook error:', error);
  }
}

export function dispatchEventSafe(el, eventName, detail) {
  if (!el) {
    return;
  }

  el.dispatchEvent(
    new CustomEvent(eventName, {
      detail: detail || {},
      bubbles: true,
    })
  );
}

export function escapeRegex(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function fetchWithRetry(url, options) {
  var cfg = Object.assign(
    {
      retries: 2,
      retryDelay: 350,
      timeout: 12000,
    },
    options || {}
  );

  var attempts = 0;

  function tryFetch() {
    attempts += 1;
    var controller = new AbortController();
    var timeoutId = window.setTimeout(function () {
      controller.abort();
    }, cfg.timeout);

    return window
      .fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        signal: controller.signal,
        headers: {
          'X-Requested-With': 'PandaFilter',
        },
      })
      .then(function (response) {
        window.clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error('HTTP ' + response.status + ' for ' + url);
        }

        return response.text();
      })
      .catch(function (error) {
        window.clearTimeout(timeoutId);

        if (attempts > cfg.retries) {
          throw error;
        }

        return new Promise(function (resolve) {
          window.setTimeout(resolve, cfg.retryDelay * attempts);
        }).then(tryFetch);
      });
  }

  return tryFetch();
}

export function normalizeUrl(url) {
  var parsed = new URL(url, window.location.href);
  parsed.hash = '';
  parsed.pathname = parsed.pathname.replace(/\/+$/, '') || '/';
  var entries = [];
  parsed.searchParams.forEach(function (value, key) {
    entries.push([key, value]);
  });
  entries.sort(function (a, b) {
    if (a[0] === b[0]) {
      return a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0;
    }
    return a[0] < b[0] ? -1 : 1;
  });
  parsed.search = '';
  entries.forEach(function (entry) {
    parsed.searchParams.append(entry[0], entry[1]);
  });
  return parsed.toString();
}

export function hashString(value) {
  var input = String(value || '');
  var hash = 5381;
  var i;
  for (i = 0; i < input.length; i += 1) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

// Webflow paginates a CMS list with `?<listId>_page=N`; plain `?page=N` elsewhere.
function isPageParam(key) {
  return key === 'page' || /_page$/.test(key);
}

// Page 1 is the same document as the URL without its page param, so Webflow's "Previous"
// link on page 2 (`?abc_page=1`) must compare equal to the bare page URL.
export function stripFirstPageParam(url) {
  var parsed = new URL(url, window.location.href);
  var keys = [];
  parsed.searchParams.forEach(function (value, key) {
    if (isPageParam(key) && value === '1') {
      keys.push(key);
    }
  });
  keys.forEach(function (key) {
    parsed.searchParams.delete(key);
  });
  return normalizeUrl(parsed.toString());
}

export function pageFromUrl(url) {
  try {
    var parsed = new URL(url, window.location.href);
    var fromQuery = null;
    parsed.searchParams.forEach(function (value, key) {
      if (fromQuery === null && isPageParam(key)) {
        fromQuery = value;
      }
    });

    if (fromQuery && /^\d+$/.test(fromQuery)) {
      return parseInt(fromQuery, 10);
    }

    var pathMatch = parsed.pathname.match(/\/page\/(\d+)\/?$/i);
    if (pathMatch) {
      return parseInt(pathMatch[1], 10);
    }

    return Number.MAX_SAFE_INTEGER;
  } catch (_error) {
    return Number.MAX_SAFE_INTEGER;
  }
}
